import { defineStore } from "pinia"
import {
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  deleteUser as sdkDeleteUser,
  signOut as sdkSignOut,
} from "firebase/auth"
import {
  doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, serverTimestamp,
} from "firebase/firestore"
import { auth, db } from "@/firebase/firebase"

// --- SDK-first, REST-with-token fallback (unchanged logic) ---
async function readUserProfile(uid) {
  if (!uid) return null

  // 1) SDK
  try {
    const snap = await getDoc(doc(db, "users", uid))
    if (snap.exists()) return { id: snap.id, ...snap.data() }
  } catch (e) {
    console.warn("[Auth] SDK profile read failed, will try REST:", e?.message || e)
  }

  // 2) REST (owner token)
  try {
    const token = await auth.currentUser?.getIdToken?.()
    if (!token) throw new Error("missing idToken")
    const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID
    const url = `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents/users/${uid}`
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    if (!r.ok) throw new Error(`HTTP ${r.status}`)
    const j = await r.json()
    const f = j.fields || {}
    const data = {}
    for (const [k, v] of Object.entries(f)) {
      if ("stringValue" in v) data[k] = v.stringValue
      else if ("integerValue" in v) data[k] = Number(v.integerValue)
      else if ("doubleValue" in v) data[k] = Number(v.doubleValue)
      else if ("booleanValue" in v) data[k] = !!v.booleanValue
      else if ("mapValue" in v) data[k] = v.mapValue?.fields || {}
      else if ("arrayValue" in v) data[k] = v.arrayValue?.values || []
    }
    return data
  } catch (e) {
    console.warn("[Auth] REST profile read failed:", e?.message || e)
    return null
  }
}

// tiny helpers
const toSlug = (s) => (s || "").trim().toLowerCase()
const delay = (ms) => new Promise((r) => setTimeout(r, ms))

async function readProfileWithRetry(uid, tries = 3, waitMs = 600) {
  for (let i = 0; i < tries; i++) {
    const p = await readUserProfile(uid)
    if (p) return p
    await delay(waitMs)
  }
  return null
}
async function ensureUserDoc(user) {
  try {
    const uid = user?.uid;
    if (!uid) return;

    const ref = doc(db, "users", uid);
    const snap = await getDoc(ref);

    // Build a safe username from displayName or email local-part
    const fromDisplay = (user.displayName || "").trim();
    const fromEmail = (user.email || "").split("@")[0] || "user";
    const base = (fromDisplay || fromEmail)
      .toLowerCase()
      .replace(/[^a-z0-9._-]/g, "")
      .slice(0, 24) || "user";
    const username = base;
    const usernameLower = base;

    if (!snap.exists()) {
      await setDoc(ref, {
        email: user.email || null,
        displayName: user.displayName || "",
        username,
        usernameLower,
        plan: "free",
        createdAt: serverTimestamp(),
      });
      console.info("[Auth] created users/{uid} doc (backfill)");
      return;
    }

    // Doc exists: backfill missing username fields if needed
    const d = snap.data() || {};
    const patch = {};
    if (!d.username) patch.username = username;
    if (!d.usernameLower) patch.usernameLower = usernameLower;
    if (!d.plan) patch.plan = "free";
    if (Object.keys(patch).length) {
      await updateDoc(ref, patch);
      console.info("[Auth] backfilled users/{uid} fields", patch);
    }
  } catch (e) {
    console.warn("[Auth] ensureUserDoc failed", e);
  }
}

export const useAuthStore = defineStore("auth", {
  state: () => ({
    user: null,
    profile: null, // { displayName, username, usernameLower, plan }
    ready: false,
    _inited: false,
  }),
  getters: {
    isSignedIn: (s) => !!s.user,
    displayName: (s) => s.profile?.displayName || s.user?.email || "",
    username: (s) => s.profile?.username || null,
  },
  actions: {
    init() {
      if (this._inited) return
      this._inited = true
      onAuthStateChanged(auth, async (u) => {
        this.user = u
        if (u) {
          await ensureUserDoc(u); // ← creates/patches doc for older accounts
          // first shot + quick retry to avoid the 404 race you saw
          this.profile = (await readUserProfile(u.uid)) || this.profile
          if (!this.profile) {
            const p = await readProfileWithRetry(u.uid, 2, 800)
            if (p) this.profile = p
          }
        } else {
          this.profile = null
        }
        this.ready = true
      })
    },

    async signUp({ email, password, displayName, username }) {
      // best-effort username uniqueness (don’t break if rules block this read)
      const candidate = toSlug(username)
      if (!candidate) throw new Error("Please choose a username.")
      try {
        const q = query(collection(db, "users"), where("usernameLower", "==", candidate))
        const existing = await getDocs(q)
        if (!existing.empty) throw new Error("Sorry, that username is taken.")
      } catch (e) {
        if (e?.code !== "permission-denied") throw e
      }

      const { user } = await createUserWithEmailAndPassword(auth, email, password)

      try { await updateProfile(user, { displayName: displayName || username }) } catch {}

      // write profile
      const ref = doc(db, "users", user.uid)
      await setDoc(ref, {
        displayName: displayName || username,
        username,
        usernameLower: candidate,
        createdAt: new Date().toISOString(),
        plan: "free",
      })

      // optimistic store update so UI shows immediately
      this.user = user
      this.profile = {
        displayName: displayName || username,
        username,
        usernameLower: candidate,
        plan: "free",
      }

      // background refresh to get the server copy
      readProfileWithRetry(user.uid, 3, 700).then((p) => { if (p) this.profile = p })
    },

    async signIn({ email, password }) {
      const { user } = await signInWithEmailAndPassword(auth, email, password)
      this.user = user
      // load non-blocking, with retry (prevents “spinner forever”)
      readProfileWithRetry(user.uid, 3, 700).then((p) => { if (p) this.profile = p })
      return { ok: true, uid: user.uid }
    },

    async sendReset(email) {
      await sendPasswordResetEmail(auth, email)
    },

    async updateDisplayName(newName) {
      if (!this.user) throw new Error("Not signed in")
      try { await updateProfile(this.user, { displayName: newName }) } catch {}
      const ref = doc(db, "users", this.user.uid)
      await updateDoc(ref, { displayName: newName })
      const p = await readProfileWithRetry(this.user.uid, 2, 600)
      if (p) this.profile = p
    },

    async deleteAccount() {
      if (!this.user) return
      const ref = doc(db, "users", this.user.uid)
      await setDoc(ref, { deletedAt: new Date().toISOString() }, { merge: true })
      await sdkDeleteUser(this.user)
      this.user = null
      this.profile = null
    },

    async signOut() {
      await sdkSignOut(auth)
      this.user = null
      this.profile = null
    },
  },
})
